import { Box, Button, Center, Checkbox, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimeEntries, type TimeEntry } from '@/api/timeEntries';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import dayjs, { type Dayjs } from '@/lib/dayjs';
import { formatDate, formatDuration, inZone, startOfWeek } from '@/lib/format';
import { BulkBar } from './BulkBar';
import { EntryRow } from './EntryRow';

interface DayGroup {
  key: string;
  day: Dayjs;
  entries: TimeEntry[];
  total: number;
}

interface WeekGroup {
  key: string;
  start: Dayjs;
  days: DayGroup[];
  total: number;
}

const similarKey = (e: TimeEntry) =>
  [e.description ?? '', e.projectId ?? '', e.taskId ?? '', [...e.tagIds].sort().join(','), e.billable, e.type].join('|');

/** Eintragsliste gruppiert nach Woche und Tag (TT-05). */
export function EntryList({ userId }: { userId?: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { workspaceId, prefs } = ctx;
  const [weeks, setWeeks] = useState(2);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectable = ctx.hasFeature('BULK_EDIT');

  const today = dayjs().tz(prefs.timezone).startOf('day');
  const weekStart = startOfWeek(today, prefs);
  const from = weekStart.subtract(weeks - 1, 'week');
  const to = weekStart.add(1, 'week');
  const { data, isPending } = useTimeEntries(workspaceId, from.toISOString(), to.toISOString(), userId);

  const grouped = useMemo<WeekGroup[]>(() => {
    const entries = (data?.items ?? []).filter((e) => e.end);
    const byWeek = new Map<string, WeekGroup>();
    for (const e of entries) {
      const local = inZone(e.start, prefs);
      const day = local.startOf('day');
      const ws = startOfWeek(day, prefs);
      const wk = ws.format('YYYY-MM-DD');
      const week = byWeek.get(wk) ?? { key: wk, start: ws, days: [], total: 0 };
      byWeek.set(wk, week);
      const dk = day.format('YYYY-MM-DD');
      let dg = week.days.find((d) => d.key === dk);
      if (!dg) {
        dg = { key: dk, day, entries: [], total: 0 };
        week.days.push(dg);
      }
      dg.entries.push(e);
      dg.total += e.type === 'BREAK' ? 0 : e.durationSeconds;
      week.total += e.type === 'BREAK' ? 0 : e.durationSeconds;
    }
    const list = [...byWeek.values()].sort((a, b) => b.key.localeCompare(a.key));
    list.forEach((w) => w.days.sort((a, b) => b.key.localeCompare(a.key)));
    return list;
  }, [data, prefs]);

  const dayLabel = (day: Dayjs) => {
    if (day.isSame(today, 'day')) return t('tracker.today');
    if (day.isSame(today.subtract(1, 'day'), 'day')) return t('tracker.yesterday');
    return `${day.format('dd')}, ${formatDate(day, prefs)}`;
  };

  const weekLabel = (start: Dayjs) => {
    if (start.isSame(weekStart, 'day')) return t('tracker.thisWeek');
    if (start.isSame(weekStart.subtract(1, 'week'), 'day')) return t('tracker.lastWeek');
    return `${formatDate(start, prefs)} – ${formatDate(start.add(6, 'day'), prefs)}`;
  };

  const toggle = (key: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setSelectedIds = (ids: string[], value: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      ids.forEach((id) => (value ? next.add(id) : next.delete(id)));
      return next;
    });

  if (isPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="lg" mt="lg">
      {selectable && selected.size > 0 && <BulkBar ids={[...selected]} onDone={() => setSelected(new Set())} />}
      {grouped.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          {t('tracker.empty')}
        </Text>
      )}
      {grouped.map((week) => (
        <Box key={week.key}>
          <Group justify="space-between" mb={4} px="xs">
            <Text size="sm" c="dimmed">
              {weekLabel(week.start)}
            </Text>
            <Text size="sm" c="dimmed">
              {t('tracker.weekTotal')}: <b>{formatDuration(week.total, prefs.durationFormat)}</b>
            </Text>
          </Group>
          <Stack gap="sm">
            {week.days.map((dayGroup) => {
              const ids = dayGroup.entries.filter((e) => !e.locked).map((e) => e.id);
              const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
              const groups = new Map<string, TimeEntry[]>();
              for (const e of dayGroup.entries) {
                const key = prefs.groupSimilarEntries ? similarKey(e) : e.id;
                groups.set(key, [...(groups.get(key) ?? []), e]);
              }
              return (
                <Paper key={dayGroup.key} withBorder radius="md" style={{ overflow: 'hidden' }}>
                  <Group justify="space-between" px="sm" py={8} bg="var(--mantine-color-gray-light)">
                    <Group gap="xs">
                      {selectable && (
                        <Checkbox
                          size="xs"
                          checked={allSelected}
                          onChange={(e) => setSelectedIds(ids, e.currentTarget.checked)}
                          aria-label={t('tracker.selectDay')}
                        />
                      )}
                      <Text size="sm" fw={500}>
                        {dayLabel(dayGroup.day)}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {t('tracker.total')}: <b>{formatDuration(dayGroup.total, prefs.durationFormat)}</b>
                    </Text>
                  </Group>
                  {[...groups.entries()].map(([key, list]) => {
                    const first = list[0]!;
                    const groupKey = `${dayGroup.key}:${key}`;
                    const isOpen = expanded.has(groupKey);
                    if (list.length === 1) {
                      return (
                        <EntryRow
                          key={first.id}
                          entry={first}
                          selectable={selectable}
                          selected={selected.has(first.id)}
                          onSelect={(v) => setSelectedIds([first.id], v)}
                        />
                      );
                    }
                    return (
                      <Fragment key={groupKey}>
                        <EntryRow
                          entry={first}
                          groupCount={list.length}
                          groupExpanded={isOpen}
                          onToggleGroup={() => toggle(groupKey)}
                          groupSeconds={list.reduce((sum, e) => sum + e.durationSeconds, 0)}
                          selectable={selectable}
                          selected={list.every((e) => selected.has(e.id))}
                          onSelect={(v) => setSelectedIds(list.map((e) => e.id), v)}
                        />
                        {isOpen &&
                          list.map((e) => (
                            <EntryRow
                              key={e.id}
                              entry={e}
                              nested
                              selectable={selectable}
                              selected={selected.has(e.id)}
                              onSelect={(v) => setSelectedIds([e.id], v)}
                            />
                          ))}
                      </Fragment>
                    );
                  })}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ))}
      <Center>
        <Button variant="subtle" onClick={() => setWeeks((w) => w + 2)}>
          {t('tracker.loadMore')}
        </Button>
      </Center>
    </Stack>
  );
}
